/**
 * @file     leaseListing.ts
 * @readme   ./CLAUDE.md
 * @purpose  Client-safe rules for a rental listing page, shared by the MLS
 *           lease variant and the owner listing page: one terms model built
 *           from either the typed MLS lease columns or an owner snapshot,
 *           move-in costs at the Residential Tenancy Act caps, the MLS
 *           sub-type to owner home-type map the fair-rent band compares on,
 *           and availability. Never invents a field the feed does not carry.
 */
import { depositCap, type LeaseAttributes } from '@/core/config/listingKinds';
import { LEASE_HOME_TYPE_SUB_TYPES, type LeaseHomeType } from '@/services/listing-submissions/rentRange';
import type { LeaseFacts } from '@/services/mls/mlsPropertyService';
import { formatShortDate } from '@/utils/dateFormat';

export const LEASE_PROPERTY_TYPE = 'Residential Lease';

export function isLeasePropertyType(propertyType: string | null | undefined): boolean {
  return propertyType === LEASE_PROPERTY_TYPE;
}

export type RentalPets = 'allowed' | 'cats' | 'dogs' | 'no' | 'ask';
export type RentalFurnished = 'yes' | 'no' | 'partly';
export type RentalLaundry = 'in_unit' | 'in_building' | 'none';
export type RentalLeaseTerm = 'short' | 'month_to_month' | 'six_months' | 'one_year' | 'other';

/** One shape for both sources. Null means the source does not say, and the row reads "Not stated" or hides. */
export interface RentalTerms {
  source: 'mls' | 'owner';
  availableOn: string | null;
  leaseTerm: RentalLeaseTerm | null;
  /** MLS only: the short-term flag (LeaseTerm 'Short Term Lease'). */
  shortTerm: boolean | null;
  pets: RentalPets | null;
  furnished: RentalFurnished | null;
  laundry: RentalLaundry | null;
  parking: number | null;
  /** MLS only (BCRES_RentersInsuranceRequiredYN). */
  rentersInsuranceRequired: boolean | null;
  /** MLS only: BCRES_TermDetails, shown verbatim. */
  termDetails: string | null;
  /** Owner only: utilities the owner says the rent includes. */
  utilitiesIncluded: string[] | null;
  /** Owner only: house rules in the owner's words, checked at review. */
  rules: string | null;
}

/** BCRES PetsAllowed arrives as a mix of Yes/No and per-animal flags. */
export function mlsPets(values: readonly string[] | null | undefined): RentalPets | null {
  if (!values || values.length === 0) return null;
  const has = (v: string) => values.includes(v);
  const cats = has('Cats OK');
  const dogs = has('Dogs OK');
  if (has('No') && !has('Yes')) return cats || dogs ? 'ask' : 'no';
  if (cats && dogs) return 'allowed';
  if (cats) return 'cats';
  if (dogs) return 'dogs';
  if (has('Yes')) return has('No Cats') && has('No Dogs') ? 'ask' : 'allowed';
  return null;
}

export function mlsFurnished(value: string | null | undefined): RentalFurnished | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v.startsWith('furnished')) return 'yes';
  if (v.startsWith('unfurnished')) return 'no';
  if (v.startsWith('partial')) return 'partly';
  return null;
}

export function mlsLaundry(values: readonly string[] | null | undefined): RentalLaundry | null {
  if (!values || values.length === 0) return null;
  if (values.some((v) => /in unit|in suite/i.test(v))) return 'in_unit';
  if (values.some((v) => /common|shared|building/i.test(v))) return 'in_building';
  if (values.some((v) => /^none$/i.test(v))) return 'none';
  return null;
}

/** A blank or dash-only BCRES_TermDetails is not a detail. */
function cleanTermDetails(value: string | null | undefined): string | null {
  const v = value?.trim() ?? '';
  return /[a-z0-9]/i.test(v) ? v : null;
}

export function termsFromMls(facts: LeaseFacts): RentalTerms {
  const short = facts.leaseTerm === 'Short Term Lease';
  return {
    source: 'mls',
    availableOn: facts.availabilityDate,
    leaseTerm: short ? 'short' : null,
    shortTerm: short,
    pets: mlsPets(facts.petsAllowed),
    furnished: mlsFurnished(facts.furnished),
    laundry: mlsLaundry(facts.laundryFeatures),
    parking: facts.parkingTotal,
    rentersInsuranceRequired: facts.rentersInsuranceRequired,
    termDetails: cleanTermDetails(facts.leaseTermDetails),
    utilitiesIncluded: null,
    rules: null,
  };
}

const OWNER_TERM: Record<LeaseAttributes['leaseTerm'], RentalLeaseTerm> = {
  month_to_month: 'month_to_month',
  six_months: 'six_months',
  one_year: 'one_year',
  other: 'other',
};
const OWNER_PETS: Record<LeaseAttributes['pets'], RentalPets> = { yes: 'allowed', cats: 'cats', dogs: 'dogs', no: 'no', ask: 'ask' };
const OWNER_FURNISHED: Record<LeaseAttributes['furnished'], RentalFurnished> = { furnished: 'yes', partly: 'partly', unfurnished: 'no' };

/** Owner attributes are validated against the lease schema at submit; anything missing reads as not stated. */
export function termsFromOwner(attributes: Partial<LeaseAttributes> | Record<string, unknown>): RentalTerms {
  const a = attributes as Partial<LeaseAttributes>;
  return {
    source: 'owner',
    availableOn: typeof a.availableOn === 'string' ? a.availableOn : null,
    leaseTerm: a.leaseTerm ? OWNER_TERM[a.leaseTerm] ?? null : null,
    shortTerm: null,
    pets: a.pets ? OWNER_PETS[a.pets] ?? null : null,
    furnished: a.furnished ? OWNER_FURNISHED[a.furnished] ?? null : null,
    laundry: a.laundry ?? null,
    parking: typeof a.parking === 'number' ? a.parking : null,
    rentersInsuranceRequired: null,
    termDetails: null,
    utilitiesIncluded: Array.isArray(a.utilitiesIncluded) ? a.utilitiesIncluded : null,
    rules: typeof a.rules === 'string' && a.rules.trim() ? a.rules.trim() : null,
  };
}

export interface MoveInCosts {
  rent: number;
  /** BC maximum: half a month (RTA s.19). */
  securityDepositMax: number;
  petDepositMax: number;
  /** First month plus both deposits at the cap. */
  totalMax: number;
  /** Owner listings: the deposits the owner set, at or under the cap. */
  securityDeposit: number | null;
  petDeposit: number | null;
}

export function moveInCosts(rent: number, owner?: { securityDeposit?: number | null; petDeposit?: number | null }): MoveInCosts {
  const cap = depositCap(rent);
  const securityDeposit = owner?.securityDeposit != null ? Math.min(owner.securityDeposit, cap) : null;
  const petDeposit = owner?.petDeposit != null ? Math.min(owner.petDeposit, cap) : null;
  const sec = securityDeposit ?? cap;
  const pet = petDeposit ?? cap;
  return { rent, securityDepositMax: cap, petDepositMax: cap, totalMax: Math.round((rent + sec + pet) * 100) / 100, securityDeposit, petDeposit };
}

/** The owner home type the rent bands compare on, from the feed's PropertySubType. Unknown types compare on bedrooms alone. */
export function leaseHomeTypeForSubType(subType: string | null | undefined): LeaseHomeType {
  if (!subType) return 'other';
  const hit = (Object.entries(LEASE_HOME_TYPE_SUB_TYPES) as Array<[LeaseHomeType, readonly string[] | null]>).find(
    ([, subTypes]) => subTypes?.includes(subType),
  );
  return hit ? hit[0] : 'other';
}

/** YYYY-MM-DD in Pacific time, so "Available now" flips at local midnight. */
export function pacificToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Vancouver', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function isAvailableNow(availableOn: string | null, today: string = pacificToday()): boolean {
  return availableOn == null ? false : availableOn <= today;
}

/** "Nov 1, 2026" from a YYYY-MM-DD, without a timezone shift. */
export function formatAvailableDate(iso: string): string {
  return formatShortDate(iso.slice(0, 10));
}

export function formatRent(n: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);
}
